/**
 * @fileoverview Notification Center store (Zustand).
 *
 * Single source of truth for the unified notification feed shared by the
 * top-right bell badge and the /admin/notifications page. Polls the unread
 * count so the badge stays fresh, refreshes it at once on a realtime event
 * (websocket, foreground push), and exposes mark-read actions that keep the
 * badge and the list in sync. The list is paginated ("load more").
 *
 * The feed belongs to ONE restaurant: on logout or a restaurant switch the
 * polling stops / the state is reset, and nothing is ever requested without a
 * restaurant id (no `/restaurant/undefined/...`).
 *
 * No mock data: everything comes from the backend aggregator. On error we
 * surface the real error and keep the previous state.
 *
 * @module stores/useNotificationCenterStore
 */

import { create } from 'zustand';
import i18n from '@/app/i18n';
import { getRestaurantId } from '@/api/client';
import {
  getNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
} from '@/api/notifications.api';
import { updateNotificationStatus } from '@/utils/hotActions';
import { wsService } from '@/services/websocket';
import { useAuthStore } from './useAuthStore';
import { useHotActionsStore } from './useHotActionsStore';

// realtime → badge: debounce of a burst, and the minimum gap between two reads of the count
export const REALTIME_DEBOUNCE_MS = 800;
export const UNREAD_MIN_GAP_MS = 5000;

const PAGE_SIZE = 30;

const currentRestaurantId = () => {
  try {
    return getRestaurantId() || null;
  } catch {
    return null;
  }
};

const EMPTY = {
  notifications: [],
  unreadCount: 0,
  hasMore: false,
  nextOffset: null,
  total: 0,
  isLoading: false,
  isLoadingMore: false,
  error: null,
};

export const useNotificationCenterStore = create((set, get) => ({
  // State
  ...EMPTY,
  filter: 'all', // 'all' | 'unread' | 'read'
  _restaurantId: null,
  _pollId: null,
  _wsCleanup: null,
  _refreshTimer: null,
  _lastUnreadAt: 0,

  /** Drop everything that belongs to the previous restaurant / session. */
  reset: () => {
    get().stopPolling();
    const t = get()._refreshTimer;
    if (t) clearTimeout(t);
    set({ ...EMPTY, filter: 'all', _restaurantId: null, _refreshTimer: null });
  },

  /** The restaurant to query, or null. Resets the feed when it changed. */
  _ensureRestaurant: () => {
    const rid = currentRestaurantId();
    if (!rid) return null;
    if (get()._restaurantId && String(get()._restaurantId) !== String(rid)) {
      set({ ...EMPTY });
    }
    if (String(get()._restaurantId) !== String(rid)) set({ _restaurantId: rid });
    return rid;
  },

  setFilter: (filter) => {
    set({ filter });
    return get().fetchList(filter);
  },

  /** Lightweight: refresh only the unread badge count. */
  refreshUnreadCount: async () => {
    const rid = get()._ensureRestaurant();
    if (!rid) return get().unreadCount;
    set({ _lastUnreadAt: Date.now() });
    try {
      const count = await getUnreadCount(rid);
      if (String(get()._restaurantId) === String(rid)) set({ unreadCount: count });
      return count;
    } catch (err) {
      // Keep the previous count; don't blank the badge on a transient error.
      return get().unreadCount;
    }
  },

  /** A realtime event (new request, broadcast, push) arrived: refresh the badge
   *  now instead of waiting for the next poll — debounced for bursts. */
  onRealtimeEvent: () => {
    // A busy restaurant sends order::new / table::available every few seconds: the badge was read
    // 2-3 times on page load (QA 19/10 round 2). One read per UNREAD_MIN_GAP_MS at most, never
    // dropped: an event right after a read waits for the end of the gap, then reads.
    const prev = get()._refreshTimer;
    if (prev) clearTimeout(prev);
    const since = Date.now() - (get()._lastUnreadAt || 0);
    const wait = Math.max(REALTIME_DEBOUNCE_MS, UNREAD_MIN_GAP_MS - since);
    const id = setTimeout(() => {
      set({ _refreshTimer: null });
      get().refreshUnreadCount();
    }, wait);
    set({ _refreshTimer: id });
  },

  /** Fetch the first page of the feed for the page. */
  fetchList: async (filter) => {
    const rid = get()._ensureRestaurant();
    const useFilter = filter ?? get().filter;
    if (!rid) return null;
    set({ isLoading: true, error: null });
    try {
      const data = await getNotifications({ filter: useFilter, limit: PAGE_SIZE, offset: 0, restaurantId: rid });
      if (String(get()._restaurantId) !== String(rid) || get().filter !== useFilter) return data;
      set({
        notifications: data.notifications ?? [],
        unreadCount: data.unread_count ?? get().unreadCount,
        hasMore: !!data.has_more,
        nextOffset: data.next_offset ?? null,
        total: data.total ?? 0,
        isLoading: false,
      });
      return data;
    } catch (err) {
      set({
        isLoading: false,
        error: err?.response?.data?.detail || err?.message || i18n.t('Could not load notifications'),
      });
      return null;
    }
  },

  /** Append the next page. */
  loadMore: async () => {
    const { hasMore, nextOffset, isLoadingMore, filter } = get();
    const rid = get()._ensureRestaurant();
    if (!rid || !hasMore || nextOffset == null || isLoadingMore) return null;
    set({ isLoadingMore: true, error: null });
    try {
      const data = await getNotifications({ filter, limit: PAGE_SIZE, offset: nextOffset, restaurantId: rid });
      if (String(get()._restaurantId) !== String(rid) || get().filter !== filter) return data;
      const seen = new Set(get().notifications.map((n) => n.uid));
      set({
        notifications: [...get().notifications, ...(data.notifications ?? []).filter((n) => !seen.has(n.uid))],
        unreadCount: data.unread_count ?? get().unreadCount,
        hasMore: !!data.has_more,
        nextOffset: data.next_offset ?? null,
        total: data.total ?? get().total,
        isLoadingMore: false,
      });
      return data;
    } catch (err) {
      set({
        isLoadingMore: false,
        error: err?.response?.data?.detail || err?.message || i18n.t('Could not load notifications'),
      });
      return null;
    }
  },

  /** Mark a single notification read; optimistic + reconcile with server count.
   *  Resolves to true / false (never rejects: the page shows a toast on false). */
  markRead: async (uid) => {
    const rid = get()._ensureRestaurant();
    if (!rid) return false;
    const prev = get().notifications;
    const target = prev.find((n) => n.uid === uid);
    if (target?.is_read) return true;
    set({
      notifications: prev.map((n) => (n.uid === uid ? { ...n, status: 'read', is_read: true } : n)),
      unreadCount: Math.max(0, get().unreadCount - (target ? 1 : 0)),
    });
    try {
      const res = await markNotificationRead(uid, rid);
      if (typeof res?.unread_count === 'number') set({ unreadCount: res.unread_count });
      // Re-filter so the item disappears from the "unread" view.
      if (get().filter !== 'all') await get().fetchList();
      return true;
    } catch (err) {
      set({ notifications: prev });
      await get().refreshUnreadCount();
      return false;
    }
  },

  /** Mark everything read. Resolves to true / false (never rejects). */
  markAllRead: async () => {
    const rid = get()._ensureRestaurant();
    if (!rid) return false;
    const prev = get().notifications;
    const prevCount = get().unreadCount;
    set({
      notifications: prev.map((n) => ({ ...n, status: 'read', is_read: true })),
      unreadCount: 0,
    });
    try {
      const res = await markAllNotificationsRead(rid);
      if (typeof res?.unread_count === 'number') set({ unreadCount: res.unread_count });
      await get().fetchList();
      return true;
    } catch (err) {
      set({ notifications: prev, unreadCount: prevCount });
      await get().refreshUnreadCount();
      return false;
    }
  },

  /** Close a customer request (hot action) from the feed: 'COMPLETED' (served)
   *  or 'DISMISSED'. A closed request is read. Resolves to true / false. */
  setHotActionStatus: async (notification, status) => {
    const rid = get()._ensureRestaurant();
    if (!rid || notification?.source !== 'hot_action') return false;
    try {
      await updateNotificationStatus(rid, notification.source_id, status);
    } catch (err) {
      return false;
    }
    set({
      notifications: get().notifications.map((n) => (n.uid === notification.uid
        ? { ...n, service_status: status.toLowerCase(), status: 'read', is_read: true }
        : n)),
    });
    // Keep the order board's request list in step (it has its own store).
    useHotActionsStore.getState().fetchNotifications?.();
    await get().refreshUnreadCount();
    if (get().filter !== 'all') await get().fetchList();
    return true;
  },

  /** Start polling the unread count for the bell. Idempotent. */
  startPolling: (intervalMs = 30000) => {
    if (get()._pollId) return;
    if (!currentRestaurantId()) return;
    get().refreshUnreadCount();
    const id = setInterval(() => {
      // Logged out in another tab / session wiped: stop instead of 401-looping.
      if (!currentRestaurantId()) {
        get().reset();
        return;
      }
      // a hidden tab does not poll: the websocket keeps the feed live (QA 13/10 C2)
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      get().refreshUnreadCount();
    }, intervalMs);
    // Anything that lands in the feed also arrives on the websocket.
    const room = `admin-restaurant:${currentRestaurantId()}`;
    const onEvent = () => get().onRealtimeEvent();
    const release = wsService.join(room, { onResync: onEvent });
    const offs = [
      'table::available', 'staff::broadcast', 'order::new', 'order::refunded', 'haccp::temperature_alert',
    ].map((ev) => wsService.on(ev, onEvent, { room }));
    set({
      _pollId: id,
      _wsCleanup: () => {
        offs.forEach((off) => off?.());
        release();
      },
    });
  },

  stopPolling: () => {
    const id = get()._pollId;
    if (id) clearInterval(id);
    get()._wsCleanup?.();
    set({ _pollId: null, _wsCleanup: null });
  },
}));

// Logout / restaurant switch: never keep (or poll) the previous restaurant's feed.
if (typeof window !== 'undefined') {
  let prevRestaurantId = useAuthStore.getState().restaurant?.id ?? null;
  useAuthStore.subscribe((state) => {
    const rid = state.restaurant?.id ?? null;
    if (String(rid) === String(prevRestaurantId)) return;
    prevRestaurantId = rid;
    const store = useNotificationCenterStore.getState();
    const wasPolling = !!store._pollId;
    store.reset();
    if (rid && wasPolling) store.startPolling();
  });
}
